import React, { useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { UploadSimpleIcon as UploadSimple, DownloadSimpleIcon as DownloadSimple, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowRightIcon as ArrowRight, FileTextIcon as FileText } from "@phosphor-icons/react";

/**
 * Bulk-import CSV of dogs — for breeders onboarding from an existing
 * database (Excel/Google Sheets export).
 *
 * Three-step flow:
 *   1. Pick a CSV → POST /preview returns parsed rows + per-row issues
 *   2. Review screen shows OK count + issue count + per-row drilldown
 *   3. Click Commit → POST /commit inserts the OK rows; issue rows are
 *      skipped (breeder fixes their CSV and re-uploads).
 *
 * Required columns: name, breed, sex. Optional: dob, color, microchip,
 * registration_number, current_owner, notes.
 */
export default function BulkImportDogs() {
  const [stage, setStage] = useState("pick"); // pick | review | done
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const fileBlobRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    fileBlobRef.current = file;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/breeder-onboarding/bulk-import-dogs/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStage("review");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async () => {
    if (!fileBlobRef.current) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileBlobRef.current);
      const { data } = await api.post("/breeder-onboarding/bulk-import-dogs/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStage("done");
      toast.success(`${data.inserted} dog${data.inserted === 1 ? "" : "s"} imported.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    window.open("/api/breeder-onboarding/bulk-import-template.csv", "_blank");
  };

  return (
    <AppShell>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="bulk-import-page">
        <header>
          <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Bulk import dogs</h1>
          <p className="text-[#6B6B6B] mt-3 leading-relaxed max-w-2xl">
            Coming over from another platform or an Excel sheet? Upload a CSV
            and we'll add all your dogs to the registry in one pass. Each row
            becomes a registered dog (status: pending admin approval, same as
            the one-by-one flow).
          </p>
        </header>

        {stage === "pick" && (
          <div className="rounded-xl border-2 border-dashed border-[#E5E5E5] bg-white p-8 text-center" data-testid="bulk-import-pick">
            <FileText size={42} weight="duotone" className="text-gold mx-auto mb-3" />
            <h2 className="font-spectral text-xl">Pick a CSV file</h2>
            <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-md mx-auto">
              Required columns: <code className="bg-[#FBFBFB] px-1.5 rounded">name</code>, <code className="bg-[#FBFBFB] px-1.5 rounded">breed</code>, <code className="bg-[#FBFBFB] px-1.5 rounded">sex</code>. Optional:
              dob, color, microchip, registration_number, current_owner, notes.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="bg-ink hover:bg-crimson text-white rounded-full"
                data-testid="bulk-import-pick-btn"
              >
                <UploadSimple size={14} weight="bold" /> Pick CSV
              </Button>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="rounded-full"
                data-testid="bulk-import-template-btn"
              >
                <DownloadSimple size={14} weight="bold" /> Download template
              </Button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </div>
        )}

        {stage === "review" && preview && (
          <div className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="bulk-import-review">
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Stat label="Rows in CSV" value={preview.total_rows} />
              <Stat label="Ready to import" value={preview.ok_count} tone="ok" />
              <Stat label="Skipped (issues)" value={preview.issue_count} tone={preview.issue_count > 0 ? "warn" : "neutral"} />
            </div>
            <div className="max-h-[480px] overflow-y-auto rounded-lg border border-[#EAEAEA]">
              <table className="w-full text-[12.5px]" data-testid="bulk-import-rows-table">
                <thead className="sticky top-0 bg-[#FBFBFB] border-b border-[#EAEAEA] text-[10px] uppercase tracking-wider text-[#6B6B6B]">
                  <tr>
                    <th className="px-3 py-2 text-left">Row</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Breed</th>
                    <th className="px-3 py-2 text-left">Sex</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F2F2F2]">
                  {preview.rows.map((r) => (
                    <tr key={r.row_number} className={r.issues.length ? "bg-[#FFF8E5]" : ""}>
                      <td className="px-3 py-2 text-[#6B6B6B]">{r.row_number}</td>
                      <td className="px-3 py-2 font-medium">{r.name || <span className="text-crimson italic">—</span>}</td>
                      <td className="px-3 py-2">{r.breed || <span className="text-crimson italic">—</span>}</td>
                      <td className="px-3 py-2 capitalize">{r.sex || <span className="text-crimson italic">—</span>}</td>
                      <td className="px-3 py-2">
                        {r.issues.length === 0 ? (
                          <span className="inline-flex items-center gap-1 text-[#0F7B3E]"><CheckCircle size={11} weight="fill" /> OK</span>
                        ) : (
                          <span className="inline-flex items-start gap-1 text-[#8A4A00]"><Warning size={11} weight="fill" className="mt-0.5" /> {r.issues.join("; ")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={onCommit}
                disabled={busy || preview.ok_count === 0}
                className="bg-ink hover:bg-crimson text-white rounded-full"
                data-testid="bulk-import-commit-btn"
              >
                Import {preview.ok_count} dog{preview.ok_count === 1 ? "" : "s"} <ArrowRight size={13} weight="bold" />
              </Button>
              <Button
                onClick={() => { setStage("pick"); setPreview(null); }}
                variant="outline"
                className="rounded-full"
              >
                Start over
              </Button>
            </div>
          </div>
        )}

        {stage === "done" && preview && (
          <div className="rounded-xl border-2 border-[#BFE8C9] bg-[#EEFBEC] p-6 text-center" data-testid="bulk-import-done">
            <CheckCircle size={42} weight="fill" className="text-[#0F7B3E] mx-auto mb-3" />
            <h2 className="font-spectral text-xl text-[#0F7B3E]">{preview.inserted} dog{preview.inserted === 1 ? "" : "s"} imported</h2>
            <p className="text-[13px] text-[#3F3F3F] mt-2">
              They're now in your <strong>My Dogs</strong> list with status <em>pending admin approval</em>.
              {preview.issue_count > 0 && ` (${preview.issue_count} row${preview.issue_count === 1 ? "" : "s"} skipped — fix the CSV and re-upload to import those.)`}
            </p>
            <div className="mt-4 flex justify-center gap-2 flex-wrap">
              <Button asChild className="bg-ink hover:bg-crimson text-white rounded-full">
                <a href="/dogs" data-testid="bulk-import-view-dogs">View my dogs <ArrowRight size={13} weight="bold" /></a>
              </Button>
              <Button
                onClick={() => { setStage("pick"); setPreview(null); }}
                variant="outline"
                className="rounded-full"
              >
                Import another file
              </Button>
            </div>
          </div>
        )}
      </section>
    </AppShell>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const toneClass = tone === "ok" ? "text-[#0F7B3E]" : tone === "warn" ? "text-[#8A4A00]" : "text-ink";
  return (
    <div className="rounded-lg border border-[#EAEAEA] bg-[#FBFBFB] p-3">
      <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold">{label}</p>
      <p className={`font-spectral text-3xl font-light leading-none mt-1 font-tabular ${toneClass}`}>{value}</p>
    </div>
  );
}
